export type FabVisibility = {
  shouldHideParentFab: boolean;
  shouldHideOnMobile: boolean;
};

/**
 * True when the current route is a create outlet (`.../new` or `.../new/...`).
 * Used to hide the parent FAB so it does not overlap the create form.
 */
export function isCreateOutletPath(pathname: string): boolean {
  return /(\/)new(\/|$)/.test(pathname);
}

/**
 * Determines FAB visibility based on detail page detection and mobile/outlet state.
 *
 * Handles hiding FABs when:
 * - Viewing detail pages (Asset/Space/Job) in tabs context
 * - On mobile devices with open outlets
 * - Parent FABs that should be hidden when viewing their detail pages
 */
export function getDetailVisibilityDetection(
  fabLabel: string,
  isViewingDetail: boolean | undefined,
  pathname: string,
  inTabsContext: boolean,
  isMobile: boolean,
  isOutletOpenGlobal: boolean,
  isWithinOutlet: boolean
): FabVisibility {
  // Identify FAB type based on label (case-insensitive)
  const normalizedLabel = fabLabel.toLowerCase();
  const isAssetFab = normalizedLabel === 'asset';
  const isSpaceFab = normalizedLabel === 'space';
  const isJobFab = normalizedLabel === 'job';

  // Determine if viewing detail page for each FAB type
  // Priority: explicit prop > pathname-based detection
  const isViewingAssetDetail =
    isViewingDetail !== undefined && isAssetFab
      ? isViewingDetail
      : !!pathname.match(/\/sites\/[^/]+\/assets\/[^/]+(\/|$)/);
  const isViewingSpaceDetail =
    isViewingDetail !== undefined && isSpaceFab
      ? isViewingDetail
      : !!pathname.match(/\/sites\/[^/]+\/spaces\/[^/]+(\/|$)/);
  const isViewingJobDetail =
    isViewingDetail !== undefined && isJobFab
      ? isViewingDetail
      : !!pathname.match(/\/sites\/[^/]+\/jobs\/[^/]+(\/|$)/);

  // Hide parent FAB when viewing its detail page in tabs context
  const shouldHideParentFab =
    inTabsContext &&
    ((isAssetFab && isViewingAssetDetail) ||
      (isSpaceFab && isViewingSpaceDetail) ||
      (isJobFab && isViewingJobDetail));

  // Hide FAB on mobile when:
  // 1. Outlet is open globally AND this FAB is NOT within the outlet (prevents overlap)
  // 2. OR when viewing a detail page that matches this parent FAB type
  const shouldHideOnMobile =
    isMobile &&
    ((isOutletOpenGlobal && !isWithinOutlet) || shouldHideParentFab);

  return {
    shouldHideParentFab,
    shouldHideOnMobile,
  };
}
